import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Edit, Loader2, CheckCircle, Archive } from 'lucide-react';
import { useStrategy } from '@/hooks/useStrategy';
import { useUpdateStrategyStatus } from '@/hooks/useUpdateStrategyStatus';

export default function StrategyDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: strategyData, isLoading, error } = useStrategy(id!);
  const updateStatus = useUpdateStrategyStatus();

  useEffect(() => {
    if (strategyData?.data) {
      document.title = `${strategyData.data.title} | Admin Dashboard`;
    }
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, [strategyData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pine-pine" />
      </div>
    );
  }

  if (error || !strategyData?.data) {
    return (
      <div className="admin-card p-6">
        <p className="text-red-400">Strategy not found</p>
        <button
          onClick={() => navigate('/admin/strategies')}
          className="mt-4 text-pine-pine"
        >
          Back to Strategies
        </button>
      </div>
    );
  }

  const strategy = strategyData.data;

  const handlePublish = () => {
    updateStatus.mutate({ id: id!, status: 'published' });
  };

  const handleArchive = () => {
    updateStatus.mutate({ id: id!, status: 'archived' });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back + Actions Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/strategies')}
          className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Strategies
        </button>
        <Link
          to={`/admin/strategies/${id}/edit`}
          className="admin-button-primary flex items-center gap-2"
        >
          <Edit className="w-4 h-4" />
          Edit Strategy
        </Link>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title & subtitle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="admin-card p-6"
          >
            <h1 className="text-pine-display font-display text-pine-ink">{strategy.title}</h1>
            <p className="text-pine-body text-pine-ink-soft mt-1">{strategy.subtitle}</p>
          </motion.div>

          {/* Image */}
          {strategy.imageUrl && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="admin-card p-6"
            >
              <img
                src={strategy.imageUrl}
                alt={strategy.title}
                className="w-full rounded-lg"
              />
            </motion.div>
          )}

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="admin-card p-6"
          >
            <h2 className="text-pine-heading font-semibold text-pine-ink mb-3">Read Aloud</h2>
            <p className="text-pine-body text-pine-ink whitespace-pre-wrap">{strategy.description}</p>
          </motion.div>

          {/* Tip */}
          {strategy.tip && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="admin-card p-6 border-l-4 border-pine-gold"
            >
              <h2 className="text-pine-heading font-semibold text-pine-gold-deep mb-2">Tip</h2>
              <p className="text-pine-body text-pine-ink whitespace-pre-wrap">{strategy.tip}</p>
            </motion.div>
          )}
        </div>

        {/* Right column - Metadata & Actions */}
        <div className="space-y-6">
          {/* Details card */}
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-pine-heading font-semibold text-pine-ink">Details</h2>

            <div className="space-y-3">
              <div>
                <span className="text-pine-caption text-pine-ink-soft uppercase tracking-wide font-semibold">Category</span>
                <p className="text-pine-body text-pine-ink mt-0.5">{strategy.category.name}</p>
              </div>
              <div>
                <span className="text-pine-caption text-pine-ink-soft uppercase tracking-wide font-semibold">Sort Order</span>
                <p className="text-pine-body text-pine-ink mt-0.5">{strategy.sortOrder}</p>
              </div>
              <div>
                <span className="text-pine-caption text-pine-ink-soft uppercase tracking-wide font-semibold">Icon</span>
                {strategy.iconImageUrl ? (
                  <img
                    src={strategy.iconImageUrl}
                    alt="Strategy icon"
                    className="w-10 h-10 object-contain mt-1"
                  />
                ) : (
                  <p className="text-pine-body text-pine-ink-soft mt-0.5">—</p>
                )}
              </div>
              <div>
                <span className="text-pine-caption text-pine-ink-soft uppercase tracking-wide font-semibold">Created</span>
                <p className="text-pine-body text-pine-ink mt-0.5">
                  {new Date(strategy.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Status Management card */}
          <div className="admin-card p-6 space-y-4">
            <h2 className="text-pine-heading font-semibold text-pine-ink">Status</h2>

            {/* Current status badge */}
            <div>
              <span
                className={`inline-flex items-center rounded-pine-pill px-3 py-1 text-pine-caption font-semibold border-[1.5px] ${
                  strategy.status === 'published'
                    ? 'text-pine-approved border-pine-approved bg-pine-approved/10'
                    : 'text-pine-draft border-pine-draft bg-pine-draft/10'
                }`}
              >
                {strategy.status === 'published' ? 'Published' : 'Archived'}
              </span>
            </div>

            {/* Action buttons */}
            <div className="space-y-2">
              {strategy.status === 'published' ? (
                <button
                  onClick={handleArchive}
                  disabled={updateStatus.isPending}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-pine-input border border-pine-line text-pine-ink-soft hover:bg-pine-hover-item transition-colors"
                >
                  <Archive className="w-4 h-4" />
                  Archive Strategy
                </button>
              ) : (
                <button
                  onClick={handlePublish}
                  disabled={updateStatus.isPending}
                  className="admin-button-primary w-full flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Publish Strategy
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
